import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError, RequiredOrderIdError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { removePrimitiveDuplicatesFromArray } from 'core/common/utils/removePrimitiveDuplicatesFromArray';
import { getEmailFlowAttributesReports, getFunnelName } from 'core/funnel/store/selectors';
import { getOfferReportTypes } from 'core/offers/utils';
import { getEmail, getUserInformation, getUserLocation } from 'core/user/store/selectors';
import { PaymentError, PaymentFormData, PaymentFormType } from '../../entities';
import { PaymentDoubleSubscriptionError, PaymentUpsellError } from '../../errors';
import { getOrderData, getOrderId } from '../selectors';
import { updateOrderData } from './fetchOrder';

export const initializePaymentFormPending = createAction(
  'payments/INITIALIZE_PAYMENT_FORM/pending',
);
export const updateOrderId = createAction<string>('payments/UPDATE_ORDER_ID');

export const initializePaymentFormSucceed = createAction<PaymentFormData>(
  'payments/INITIALIZE_PAYMENT_FORM/succeed',
);
export const initializePaymentFormFailed = createAction<PaymentError>(
  'payments/INITIALIZE_PAYMENT_FORM/failed',
);

export const initializePaymentFormRequest =
  (offerId: string, paymentFormType: PaymentFormType): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      loggingService: logger,
      observabilitySystem,
      errorMonitoringService,
      paymentService,
      funnelSettingsService,
      subscriptionService,
    } = context;

    const storeOrderId = getOrderId(getState());
    const user = getUserInformation(getState());
    const userLocation = getUserLocation(getState());
    const email = getEmail(getState());
    const funnelName = getFunnelName(getState());
    const orderData = getOrderData(getState());

    const emailFlowAttributesReports = getEmailFlowAttributesReports(getState());
    const reportTypes = getOfferReportTypes(emailFlowAttributesReports, offerId);

    dispatch(initializePaymentFormPending());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: funnelSettingsService.getFlowName(),
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelSettingsService.getFunnelLocale(),
    };

    const span = observabilitySystem.startSpan(SpanName.INITIALIZE_FORM_REQUEST);
    span.setAttributes(spanAttributes);

    const orderId = storeOrderId || user.orderId;

    try {
      if (!orderId) {
        throw new RequiredOrderIdError();
      }

      if (paymentFormType === PaymentFormType.SUBSCRIPTION) {
        const isActiveSubscription = await subscriptionService.isActiveSubscription();

        if (isActiveSubscription) {
          throw new PaymentDoubleSubscriptionError();
        }
      }

      if (paymentFormType === PaymentFormType.UPSELL) {
        const activeUpsellPayments = await paymentService.getPayments({ offerId, email });

        if (!!activeUpsellPayments.length) {
          throw new PaymentUpsellError();
        }
      }

      const reports = Array.isArray(orderData.reports) ? orderData.reports : [];
      const mergedReports = removePrimitiveDuplicatesFromArray([...reports, ...reportTypes]);

      await paymentService.updateOrder({
        orderId,
        offerId,
        extra_data: {
          reports: mergedReports,
        },
      });

      const formData = await paymentService.createPayment({
        email,
        orderId,
        offerId,
      });

      span.succeed();

      dispatch(initializePaymentFormSucceed(formData));
      dispatch(updateOrderData({ ...orderData, reports: mergedReports }));
    } catch (err) {
      const error = normalizeError(err);
      logger.error(error);

      span.fail(error);
      errorMonitoringService.captureError(error, {
        tags: {
          offer_id: offerId,
          order_id: orderId,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'initialize_form_failed',
          funnel_name: funnelName,
          ...userLocation,
        },
      });

      dispatch(initializePaymentFormFailed({ message: error.message }));
    }
  };

export const paymentFormVerify = createAction(`payments/PAYMENT_FORM_VERIFY`);

export const paymentFormFailed = createAction<PaymentError>(`payments/PAYMENT_FORM_FAILED`);

export const paymentFormSucceed = createAction(`payments/PAYMENT_FORM_SUCCEED`);
